import type {
  PublicFormApiResponse,
  PublicFormApiUser,
  StudentFormLoadData,
  StudentFormSubmitData,
  StudentFormSubmitRequest,
} from './api'

export type ProgressState = 'active' | 'success' | 'error'

export type ImpersonationLoginState = {
  loginValidated: false
  impersonateMessageVisible: true
  username: string
  password: 'test'
}

export type StoredDeploymentUserState = {
  deploymentMode: string | null
  userName: string | null
  permissions: string | null
}

export type DeploymentUserState = {
  deploymentMode: string
  defaultDeployModeStorageValue: string | undefined
  userName: string | null
  permissions: string | null
  userAuthenticated: boolean
  userNameStorageValue: string | null | undefined
  permissionsStorageValue: string | null | undefined
}

export type PageLoadState = {
  pageLoadMessage: string
  pageLoadMessageIsError: boolean
  studentDetailsVisible: boolean
}

type DeploymentUserResponse = Partial<Omit<PublicFormApiResponse<unknown>, 'user'>> & {
  user?: PublicFormApiUser | null | false
}

export function alertBoxClass(isError: boolean): Record<string, boolean> {
  return {
    'alert-info': !isError,
    'alert-danger': isError,
  }
}

export function progressClass(state: ProgressState): Record<string, boolean> {
  return {
    active: state === 'active',
    'progress-bar-success': state === 'success',
    'bg-success': state === 'success',
    'progress-bar-danger': state === 'error',
    'bg-danger': state === 'error',
  }
}

export function legacyQueryParameter(url: string, name: string): string | null {
  const normalizedName = name.replace(/[[\]]/g, '\\$&')
  const regex = new RegExp(`[?&]${normalizedName}(=([^&#]*)|&|#|$)`)
  const results = regex.exec(url)

  if (!results) return null
  if (!results[2]) return ''

  return decodeURIComponent(results[2].replace(/\+/g, ' '))
}

export function impersonationLoginState(url: string): ImpersonationLoginState | null {
  const impersonate = legacyQueryParameter(url, 'impersonate')
  if (!impersonate) return null

  return {
    loginValidated: false,
    impersonateMessageVisible: true,
    username: impersonate,
    password: 'test',
  }
}

export function buildStudentFormSubmitRequest(secureData: string): StudentFormSubmitRequest {
  return {
    secure_data: secureData,
  }
}

export function submittedIncidentReference(data: StudentFormSubmitData): string {
  return data.incident.incidentReference
}

export function buildMaintenancePageLoadState(
  data: StudentFormLoadData,
  user: PublicFormApiResponse<StudentFormLoadData>['user'],
  sanitizeMessage: (html: string) => string,
): PageLoadState {
  const maintenanceModeEnabled = data.maintenance_mode_enabled ?? 0

  if (maintenanceModeEnabled > 0) {
    return {
      pageLoadMessage: sanitizeMessage(data.maintenance_mode_message ?? ''),
      pageLoadMessageIsError: true,
      studentDetailsVisible: Boolean(user && maintenanceModeEnabled < 2),
    }
  }

  return {
    pageLoadMessage: '',
    pageLoadMessageIsError: false,
    studentDetailsVisible: true,
  }
}

export function buildDeploymentUserState(
  response: DeploymentUserResponse,
  stored: StoredDeploymentUserState,
): DeploymentUserState {
  const responseDeploymentMode = response.deployment_mode
  const deploymentMode = responseDeploymentMode || stored.deploymentMode || ''
  const defaultDeployModeStorageValue = responseDeploymentMode || undefined

  const hasUser = Object.prototype.hasOwnProperty.call(response, 'user')
  const user = response.user

  if (hasUser && user) {
    const userName = typeof user === 'object' && user.display_name ? user.display_name : null
    const permissions = typeof user === 'object' && user.permissions
      ? Object.values(user.permissions).join(' ')
      : null

    return {
      deploymentMode,
      defaultDeployModeStorageValue,
      userName,
      permissions,
      userAuthenticated: Boolean(userName),
      userNameStorageValue: userName ?? '',
      permissionsStorageValue: permissions ?? undefined,
    }
  }

  if (hasUser && (user === false || user === null)) {
    return {
      deploymentMode,
      defaultDeployModeStorageValue,
      userName: '',
      permissions: null,
      userAuthenticated: false,
      userNameStorageValue: null,
      permissionsStorageValue: null,
    }
  }

  return {
    deploymentMode,
    defaultDeployModeStorageValue,
    userName: stored.userName,
    permissions: stored.permissions,
    userAuthenticated: Boolean(stored.userName),
    userNameStorageValue: undefined,
    permissionsStorageValue: undefined,
  }
}
